import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Flower2, Wind, Sparkles, Play, Square, Shuffle, Plus, X, AudioLines, Flame, Trophy, Check, Coins } from "lucide-react";
import Seo from "@/components/Seo";
import SoundHealing from "@/components/SoundHealing";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShareButton } from "@/components/ShareButton";

const TECHNIQUES = {
  box: { name: "Box Breathing", note: "Calm & focus (4-4-4-4)", phases: [
    { label: "Breathe in", secs: 4, scale: 1.35 }, { label: "Hold", secs: 4, scale: 1.35 },
    { label: "Breathe out", secs: 4, scale: 0.72 }, { label: "Hold", secs: 4, scale: 0.72 }] },
  calm: { name: "4-7-8 Calm", note: "Ease anxiety, drift to sleep", phases: [
    { label: "Breathe in", secs: 4, scale: 1.35 }, { label: "Hold", secs: 7, scale: 1.35 },
    { label: "Breathe out", secs: 8, scale: 0.7 }] },
  deep: { name: "Deep Calm", note: "Simple, grounding (5-5)", phases: [
    { label: "Breathe in", secs: 5, scale: 1.35 }, { label: "Breathe out", secs: 5, scale: 0.72 }] },
};

const AFFIRMATIONS = [
  "I am whole, worthy and enough, exactly as I am.",
  "I attract kindness, and I give it freely.",
  "Abundance flows to me and through me to others.",
  "I am at peace with what was, and open to what will be.",
  "My presence is a gift to this one global family.",
  "I release fear and welcome love in its place.",
  "Every breath returns me to calm and clarity.",
  "I am part of the one supreme consciousness — never alone.",
  "What I seek is also seeking me.",
  "I choose thoughts that heal, uplift and expand me.",
  "I am grateful for this moment; it holds everything I need.",
  "I am healthy, I am strong, I am deeply loved.",
];

function Meditate({ onComplete }) {
  const [key, setKey] = useState("box");
  const [running, setRunning] = useState(false);
  const [idx, setIdx] = useState(0);
  const [cycles, setCycles] = useState(0);
  const timer = useRef(null);
  const logged = useRef(false);
  const tech = TECHNIQUES[key];
  const phase = tech.phases[idx];

  useEffect(() => {
    if (!running) return;
    timer.current = setTimeout(() => {
      setIdx((p) => {
        const next = (p + 1) % tech.phases.length;
        if (next === 0) setCycles((c) => c + 1);
        return next;
      });
    }, phase.secs * 1000);
    return () => clearTimeout(timer.current);
  }, [running, idx, key]); // eslint-disable-line

  // Completing 2 full cycles counts as today's practice.
  useEffect(() => {
    if (cycles >= 2 && !logged.current) { logged.current = true; onComplete && onComplete(); }
  }, [cycles]); // eslint-disable-line

  const start = () => { setIdx(0); setCycles(0); logged.current = false; setRunning(true); };
  const stop = () => { setRunning(false); clearTimeout(timer.current); setIdx(0); };
  const pick = (k) => { stop(); setKey(k); };

  return (
    <div data-testid="meditate-panel">
      <div className="flex flex-wrap gap-2">
        {Object.entries(TECHNIQUES).map(([k, t]) => (
          <button key={k} data-testid={`tech-${k}`} onClick={() => pick(k)}
            className={`text-sm rounded-full px-4 py-2 border transition-colors ${key === k ? "bg-brand text-black border-brand font-medium" : "rud-card border-transparent text-sec"}`}>
            {t.name}
          </button>
        ))}
      </div>
      <p className="text-sec text-sm mt-3">{tech.note}</p>

      <div className="grid place-items-center py-10">
        <div className="relative w-64 h-64 grid place-items-center">
          <motion.div
            animate={{ scale: running ? phase.scale : 1 }}
            transition={{ duration: running ? phase.secs : 0.6, ease: "easeInOut" }}
            className="absolute w-40 h-40 rounded-full bg-gradient-to-br from-brand/40 to-brand/10 border border-brand/40"
            data-testid="breathing-circle"
          />
          <div className="relative text-center">
            <div className="font-serif text-2xl" data-testid="breath-phase">{running ? phase.label : "Ready"}</div>
            {running && <div className="text-sec text-sm mt-1">{cycles} {cycles === 1 ? "cycle" : "cycles"}</div>}
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        {!running ? (
          <button data-testid="breath-start" onClick={start} className="btn-primary rounded-full h-11 px-6 flex items-center gap-2"><Play size={16} /> Begin</button>
        ) : (
          <button data-testid="breath-stop" onClick={stop} className="btn-ghost rounded-full h-11 px-6 flex items-center gap-2"><Square size={16} /> Finish</button>
        )}
      </div>
    </div>
  );
}

function Manifest() {
  const [i, setI] = useState(() => Math.floor(Math.random() * AFFIRMATIONS.length));
  const [intentions, setIntentions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("anxya_intentions") || "[]"); } catch { return []; }
  });
  const [draft, setDraft] = useState("");
  const save = (list) => { setIntentions(list); localStorage.setItem("anxya_intentions", JSON.stringify(list)); };
  const add = () => { if (!draft.trim()) return; save([{ text: draft.trim(), at: Date.now() }, ...intentions].slice(0, 30)); setDraft(""); };
  const remove = (at) => save(intentions.filter((x) => x.at !== at));

  return (
    <div data-testid="manifest-panel">
      <div className="rud-card rounded-2xl p-8 text-center relative overflow-hidden" data-testid="affirmation-card">
        <div className="absolute -top-8 -right-8 w-40 h-40 brand-glow rounded-full pointer-events-none" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand mb-3">Today's affirmation</div>
        <motion.p key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="font-serif text-2xl sm:text-3xl leading-snug">
          “{AFFIRMATIONS[i]}”
        </motion.p>
        <button data-testid="affirmation-shuffle" onClick={() => setI((p) => (p + 1 + Math.floor(Math.random() * (AFFIRMATIONS.length - 1))) % AFFIRMATIONS.length)}
          className="btn-ghost rounded-full h-10 px-5 mt-6 inline-flex items-center gap-2 text-sm"><Shuffle size={15} /> New affirmation</button>
      </div>

      <div className="mt-8">
        <div className="text-sm font-medium mb-2">Set your own intention</div>
        <div className="flex gap-2">
          <input data-testid="intention-input" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="I am manifesting…" className="rud-input flex-1 rounded-lg px-4 py-2.5 text-sm" />
          <button data-testid="intention-add" onClick={add} className="btn-primary rounded-lg h-11 px-4 flex items-center gap-1"><Plus size={16} /> Hold</button>
        </div>
        <p className="text-[11px] text-sec mt-2">Your intentions are kept privately on this device. Revisit them, feel them, let them unfold.</p>
        <div className="mt-4 space-y-2" data-testid="intention-list">
          {intentions.length === 0 && <p className="text-sec text-sm">No intentions yet — plant your first seed above. 🌱</p>}
          {intentions.map((it) => (
            <div key={it.at} className="rud-card rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-testid="intention-item">
              <span className="text-sm">✨ {it.text}</span>
              <button onClick={() => remove(it.at)} className="text-sec hover:text-red-400"><X size={15} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PracticeStreak({ streak, onCheckin }) {
  const done = !!streak?.checked_in_today;
  return (
    <div className="rud-card rounded-2xl p-4 sm:p-5 mt-6 flex items-center gap-4 flex-wrap" data-testid="practice-streak">
      <div className="flex items-center gap-4 flex-1 min-w-[220px]">
        <div className="w-12 h-12 rounded-full grid place-items-center bg-brand/15 border border-brand/40 shrink-0">
          <Flame size={22} className="text-brand" />
        </div>
        <div>
          <div className="font-serif text-2xl leading-none" data-testid="practice-streak-count">
            {streak?.streak ?? 0}<span className="text-sm text-sec font-sans"> day{(streak?.streak ?? 0) === 1 ? "" : "s"}</span>
          </div>
          <div className="text-[11px] text-sec mt-1 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Trophy size={12} /> Best {streak?.best ?? 0}</span>
            <span className="inline-flex items-center gap-1" data-testid="practice-points"><Coins size={12} /> {streak?.vault ?? 0} points</span>
          </div>
        </div>
      </div>
      <button
        data-testid="practice-checkin-btn"
        onClick={onCheckin}
        disabled={done}
        className={`rounded-full h-11 px-5 text-sm font-medium flex items-center gap-2 transition-colors ${done ? "bg-white/5 text-sec border border-white/10 cursor-default" : "btn-primary"}`}
      >
        {done ? <><Check size={16} /> Practised today</> : <><Flame size={16} /> Log today's practice · +2</>}
      </button>
    </div>
  );
}

export default function Practice() {
  const { user, refreshUser } = useApp();
  const [tab, setTab] = useState("meditate");
  const [streak, setStreak] = useState(null);
  const loggingRef = useRef(false);

  useEffect(() => {
    if (!user) { setStreak(null); return; }
    api.get("/practice/streak").then((r) => setStreak(r.data)).catch(() => {});
  }, [user]);

  const checkin = async () => {
    if (!user) { toast("Sign in to track your practice and earn points."); return; }
    if (loggingRef.current) return;
    loggingRef.current = true;
    try {
      const { data } = await api.post("/practice/checkin", {});
      setStreak((s) => ({ ...(s || {}), streak: data.streak, best: data.best, total: data.total, vault: data.vault, checked_in_today: true }));
      if (data.already) toast("Already logged today ✓ — keep the streak alive!");
      else { toast.success(`🔥 ${data.streak}-day streak · +${data.awarded} points earned!`); refreshUser?.(); }
    } catch (e) { toast.error("Couldn't log your practice — please try again."); }
    finally { loggingRef.current = false; }
  };

  const TABS = [
    { k: "meditate", label: "Meditate", icon: Wind },
    { k: "sound", label: "Sound Healing", icon: AudioLines },
    { k: "manifest", label: "Manifest", icon: Sparkles },
  ];
  return (
    <div className="max-w-2xl mx-auto px-5 pt-24 pb-16" data-testid="practice-page">
      <Seo
        title="Daily Practice — Yoga Breathing, Meditation & Mindfulness"
        description="A quiet space to breathe, meditate, manifest and pray. Guided yoga breathing (box, 4-7-8), meditation and mindfulness for calm, wellbeing and longevity."
        keywords="meditation, yoga breathing, pranayama, mindfulness, breathwork, box breathing, 4-7-8, calm, wellbeing, longevity, prayer"
        path="/practice"
      />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2"><Flower2 className="text-brand" /><h1 className="font-serif text-4xl tracking-tight">Daily Practice</h1></div>
        <ShareButton slug="practice" title="Daily Practice · My Kind" text="Breathe, meditate & heal with sound on My Kind." label="Share" className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-2" />
      </div>
      <p className="text-sec text-sm mt-2">A quiet space to breathe, heal with sound and manifest — to feel calm, connected and part of the one supreme consciousness. 🕊️</p>

      <PracticeStreak streak={streak} onCheckin={checkin} />

      <div className="inline-flex gap-1 p-1 rounded-full bg-white/5 border border-white/10 mt-6" data-testid="practice-tabs">
        {TABS.map(({ k, label, icon: I }) => (
          <button key={k} data-testid={`practice-tab-${k}`} onClick={() => setTab(k)}
            className={`h-9 px-4 rounded-full text-sm flex items-center gap-1.5 transition-colors ${tab === k ? "bg-brand text-black font-medium" : "text-sec"}`}>
            <I size={15} /> {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "meditate" && <Meditate onComplete={checkin} />}
        {tab === "sound" && <SoundHealing />}
        {tab === "manifest" && <Manifest />}
      </div>
    </div>
  );
}
